import React from "react";
import { FaWhatsapp, FaLinkedin, FaGithub, FaEnvelope } from "react-icons/fa6";
import { motion } from "framer-motion";
import cvData from "../data/cvData";
import projects from "../data/projectsData";
import BrandCarousel from "./BrandCarousel";
import { staggerContainer, staggerItem, easing } from "../utils/motionVariants";
import "./HeroBanner.css";

export default function HeroBanner({ onCalendlyClick = () => {} }) {
  const { name, title, contacts } = cvData;
  const cvUrl = "/cv/Hirun-Kolongahapitiya_CV.pdf";
  const whatsappNumber = (contacts.phone || "").replace(/\D/g, "");
  const whatsappText = encodeURIComponent("Hi Hirun, I came from your portfolio website and would like to chat.");
  const whatsappUrl = whatsappNumber ? `https://wa.me/${whatsappNumber}?text=${whatsappText}` : null;

  const stats = [
    { label: "Products & launches", value: `${Math.max(projects.length, 9)}+` },
    { label: "Professional roles", value: "3" },
    { label: "Mentors & references", value: "2" },
  ];

  const socials = [
    { icon: <FaLinkedin />, href: contacts.linkedin, label: "LinkedIn" },
    { icon: <FaGithub />, href: contacts.github || "https://github.com/hirun-kolongahapitiya", label: "GitHub" },
    { icon: <FaEnvelope />, href: `mailto:${contacts.email}`, label: "Email" },
  ];

  return (
    <section className="hero-banner">
      <motion.div
        className="hero-content"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.p className="hero-eyebrow" variants={staggerItem}>
          Full Stack Software Engineer
        </motion.p>
        <motion.h1 variants={staggerItem}>
          Hi, I'm {name}. <span>Let's build scalable digital products.</span>
        </motion.h1>
        <motion.p className="hero-summary" variants={staggerItem}>
          <p>Full Stack Software Engineer with 2+ years of experience building and shipping production-ready web and mobile 
            applications using React, Next.js, Laravel, Node.js, and Python. 
            I specialize in end-to-end development, from APIs and databases to modern frontends, AI integrations, and cloud deployment.</p>
        </motion.p>

        <motion.div className="hero-actions" variants={staggerItem}>
          <motion.button
            type="button"
            className="hero-btn primary"
            onClick={onCalendlyClick}
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.2, ease: easing }}
          >
            Schedule a call
          </motion.button>
          <motion.a
            className="hero-btn secondary"
            href={`mailto:${contacts.email}`}
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.2, ease: easing }}
          >
            Email me
          </motion.a>
          <motion.a
            className="hero-link"
            href={cvUrl}
            download
            whileHover={{ x: 4 }}
            transition={{ duration: 0.2, ease: easing }}
          >
            Download CV
          </motion.a>
        </motion.div>

        <motion.div className="hero-stats" variants={staggerItem}>
          {stats.map((stat, idx) => (
            <motion.div
              className="hero-stat"
              key={stat.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + idx * 0.1, duration: 0.4, ease: easing }}
              whileHover={{ y: -4 }}
            >
              <strong>{stat.value}</strong>
              <small>{stat.label}</small>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        className="hero-visual"
        initial={{ opacity: 0, x: 32 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: easing }}
      >
        <motion.div
          className="hero-photo"
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <img
            src="/assets/profile.jpg"
            alt={`${name}, ${title} based in Colombo, Sri Lanka`}
          />
        </motion.div>
        <motion.div
          className="hero-note"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.4, ease: easing }}
          whileHover={{ scale: 1.03, rotate: -1 }}
        >
          <p>Let's chat on WhatsApp</p>
          {whatsappUrl ? (
            <a href={whatsappUrl} target="_blank" rel="noreferrer">
              <FaWhatsapp /> Fast replies
            </a>
          ) : (
            <span>Quick responses guaranteed</span>
          )}
        </motion.div>
        <motion.div
          className="hero-socials"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {socials.map((social) => (
            <motion.a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noreferrer"
              aria-label={social.label}
              variants={staggerItem}
              whileHover={{ scale: 1.08, y: -3 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2, ease: easing }}
            >
              {social.icon}
              <span>{social.label}</span>
            </motion.a>
          ))}
        </motion.div>
      </motion.div>

      <BrandCarousel />
    </section>
  );
}
